import { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { fetchMapping, saveMapping, fetchCsvInfo, CATEGORIES, CATEGORY_OUTPUT_LABELS } from "../lib/api";
import { Save, Check } from "lucide-react";

const MAP_FIELDS = [
  { key: "reference", label: "Reference #", required: false, hints: [/^reference\s*#?$/i, /\breference\b/i, /\bref\b/i] },
  { key: "name", label: "Name", required: false, hints: [/^name\s*\*?$/i, /\bname\b/i, /\btitle\b/i] },
  { key: "ean13", label: "EAN13", required: false, hints: [/\bean\s*13\b/i, /\bean\b/i, /\bbarcode\b|\bgtin\b/i] },
  { key: "brand", label: "Manufacturer/Brand", required: true, hints: [/^brand$/i, /\bbrand\b/i, /\bmake\b|\bmarque\b|\bmarka\b/i, /manufacturer/i] },
  { key: "categories", label: "Categories", fixed: true },
  { key: "cost", label: "Price Tax Excluded", required: true, hints: [/^net(to)?\s*price$/i, /\bnetto\b/i, /\bnet\b.*price|price.*\bnet\b/i, /price tax excluded/i, /\bcost\b|\bbuy\b|\bpurchase\b|wholesale/i, /price/i] },
  { key: "qty", label: "Quantity", required: true, hints: [/^stock/i, /\bstock\b/i, /\bqty\b|\bquant/i, /\bavail|\bcount\b|on.?hand/i] },
  { key: "active", label: "Active (0/1)", fixedValue: "1" },
  { key: "available", label: "Available for Order (0/1)", fixedValue: "0" },
  { key: "width", label: "Width", required: false, hints: [/width|szerok|breite/i], derivedFor: ["car"] },
  { key: "profile", label: "Aspect Ratio", required: false, hints: [/aspect\s*ratio/i, /profile|height|ratio/i], derivedFor: ["car"] },
  { key: "rim", label: "Rim (Diameter)", required: false, hints: [/rim.*diameter/i, /rim|inch|diam|wheel/i], derivedFor: ["car"] },
  { key: "load_index", label: "Load Index (LI)", required: false, hints: [/^load\s*index$/i, /load.?index/i, /\bli\b/i] },
  { key: "speed_rating", label: "Speed Rating (SI)", required: false, hints: [/^speed\s*rating$/i, /speed.?rating|speed.?index/i, /\bsi\b/i] },
  { key: "season", label: "Season", required: false, hints: [/\bseason\b/i] },
  { key: "runflat", label: "Runflat", required: false, hints: [/\brunflat\b|run.?flat/i] },
  { key: "fuel_efficiency", label: "Fuel Efficiency", required: false, hints: [/fuel.?efficiency/i] },
  { key: "wet_grip", label: "Wet Grip", required: false, hints: [/wet.?grip/i] },
  { key: "snow_grip", label: "Snow Grip", required: false, hints: [/snow.?grip/i] },
  { key: "ice_grip", label: "Ice Grip", required: false, hints: [/ice.?grip/i] },
  { key: "image_urls", label: "Image URLs", required: false, hints: [/image.?url/i, /\bimages?\b/i] },
  { key: "ev", label: "EV (0/1)", required: false, hints: [/^ev\s*\(?0\/1\)?$/i, /\bev\b/i], fixedValueFor: { motorbike: "0" } },
  { key: "van_tyres", label: "Van Tyres (0/1)", required: false, hints: [/van\s*tyres?/i], fixedValueFor: { motorbike: "0" } },
];

export { MAP_FIELDS };

// A field is "fixed" (non-mappable) if it's globally fixed, fixed for this
// category specifically, has a literal fixedValue (fixed for all categories),
// has a category-specific fixed value, or is derived/computed for this
// category (e.g. parsed from another column).
function isFixed(f, category) {
  return !!(
    f.fixed ||
    f.fixedFor?.includes(category) ||
    f.fixedValue !== undefined ||
    f.fixedValueFor?.[category] !== undefined ||
    f.derivedFor?.includes(category)
  );
}

// Resolves the literal value to show/save for a fixed field, or undefined if
// it's fixed-to-empty (M&S-style) rather than a specific literal.
function resolvedFixedValue(f, category) {
  if (f.fixedValueFor?.[category] !== undefined) return f.fixedValueFor[category];
  if (f.fixedValue !== undefined) return f.fixedValue;
  return undefined;
}

export default function DataMapping({ mappingByCategory, setMappingFor }) {
  return (
    <div className="space-y-6">
      {CATEGORIES.map((c) => (
        <CategoryMappingPanel
          key={c.value}
          category={c.value}
          categoryLabel={c.label}
          mapping={mappingByCategory[c.value] || {}}
          setMapping={(updater) => setMappingFor(c.value, updater)}
        />
      ))}
    </div>
  );
}

function CategoryMappingPanel({ category, categoryLabel, mapping, setMapping }) {
  const [csvInfo, setCsvInfo] = useState(null);
  const [loadedSaved, setLoadedSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const headers = useMemo(() => csvInfo?.headers || [], [csvInfo]);
  const loaded = !!csvInfo?.loaded;

  // Fetch this category's CSV info once (independent of Execution Center's active category)
  useEffect(() => {
    fetchCsvInfo(category)
      .then(setCsvInfo)
      .catch(() => toast.error(`Failed to read ${categoryLabel} CSV info`));
  }, [category, categoryLabel]);

  // Load previously saved mapping once on mount
  useEffect(() => {
    fetchMapping(category)
      .then((saved) => {
        const cleaned = { ...(saved || {}) };
        // Force any field fixed for this category to its fixed value (or empty),
        // even if an earlier save had mapped it to a real column.
        MAP_FIELDS.forEach((f) => {
          if (f.fixedFor?.includes(category)) cleaned[f.key] = "";
          if (f.derivedFor?.includes(category)) cleaned[f.key] = "";
          const fixedVal = resolvedFixedValue(f, category);
          if (fixedVal !== undefined) cleaned[f.key] = fixedVal;
        });
        if (Object.keys(cleaned).length) {
          setMapping((m) => ({ ...cleaned, ...m }));
        }
      })
      .catch(() => toast.error(`Failed to load saved ${categoryLabel} mapping`))
      .finally(() => setLoadedSaved(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Auto-detect mapping when CSV loads (only fills gaps, never overrides a saved/chosen value)
  useEffect(() => {
    if (!headers.length || !loadedSaved) return;
    const next = {};
    MAP_FIELDS.forEach((f) => {
      if (isFixed(f, category)) return;
      // Try each pattern in priority order; first hit wins
      for (const pat of f.hints) {
        const match = headers.find((h) => pat.test(h));
        if (match) { next[f.key] = match; break; }
      }
    });
    setMapping((m) => ({ ...next, ...m }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, loadedSaved]);

  const updateField = (key, value) => {
    setMapping((m) => ({ ...m, [key]: value === "__none__" ? "" : value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveMapping(mapping, category);
      setMapping(saved);
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      toast.success(`${categoryLabel} mapping saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-[#E2E8F0] bg-white sharp">
      <div className="border-b border-[#E2E8F0] bg-[#F8F9FA] px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow text-[#64748B]">{categoryLabel} — Data Mapping</div>
          <div className="text-xs text-[#64748B] mt-1">
            {loaded ? (
              <>Source: <span className="font-mono-data">{csvInfo.filename}</span></>
            ) : (
              "Map CSV columns to the fields the pricing engine expects."
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {savedFlash && (
            <span className="text-[12px] text-[#16A34A] flex items-center gap-1 slide-in font-semibold">
              <Check size={14} /> Saved
            </span>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="rounded-sm h-8 text-xs gap-2 bg-[#1A2340] hover:bg-[#253060] text-white disabled:opacity-40"
            data-testid={`save-mapping-button-${category}`}
          >
            <Save size={13} /> {saving ? "Saving…" : "Save Mapping"}
          </Button>
        </div>
      </div>
      <div className="p-6">
        {!loaded ? (
          <div className="text-sm text-[#64748B]">
            No CSV loaded for {categoryLabel} yet. Use "Load Data" in the Execution Center (with this category selected) to fetch it, then map columns here.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {MAP_FIELDS.map((f) => {
              const isFixedHere = isFixed(f, category);
              return (
                <div key={f.key}>
                  <Label className="eyebrow text-[#64748B] flex items-center gap-1">
                    {f.label} {f.required && <span className="text-[#E11D48]">*</span>}
                  </Label>
                  {isFixedHere ? (
                    <div
                      className="mt-2 h-9 rounded-sm border border-[#E2E8F0] bg-[#F8F9FA] text-[13px] text-[#1A2340] font-semibold flex items-center px-3"
                      data-testid={`${f.key}-fixed-value-${category}`}
                    >
                      {f.fixed ? (
                        CATEGORY_OUTPUT_LABELS[category] || category
                      ) : resolvedFixedValue(f, category) !== undefined ? (
                        resolvedFixedValue(f, category)
                      ) : f.derivedFor?.includes(category) ? (
                        <span className="text-[#64748B] font-normal italic">Derived from Measurecode</span>
                      ) : (
                        <span className="text-[#94a3b8] font-normal italic">Empty</span>
                      )}
                    </div>
                  ) : (
                    <Select
                      value={mapping[f.key] || "__none__"}
                      onValueChange={(v) => updateField(f.key, v)}
                    >
                      <SelectTrigger
                        className="mt-2 rounded-sm border-[#E2E8F0] text-[13px]"
                        data-testid={`map-${category}-${f.key}-select`}
                      >
                        <SelectValue placeholder="— Select —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {headers.map((h) => (
                          <SelectItem key={h} value={h}>
                            {h}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
